import React from "react";
import {
  DOT_R,
  FIGURE_WIDTH,
  HEIGHT,
  dotXs,
  layout,
  lineY,
  readingNow,
  travellers
} from "./lib/stageGeometry";

// The move being made, drawn large, with the dots actually travelling.
//
// This is the animation. The shield below is a map of the whole working and
// stays a map; here one move fills the width of the page, and a line of dots
// is seen to LEAVE the figure it was read from and arrive in the figure being
// built. That is the claim the section makes — eleven of these fifteen figures
// were not cast — and it is not a claim a reader can check by being told.
//
// Three moves, drawn as three different things, because they are:
//   cast       nothing is read. Dots fall onto four Mothers, four lines each,
//              in the order the rows of points were made.
//   transpose  a line moves intact. Its dots travel one for one and land, and
//              the line they came from is banded while they are in flight.
//   add        two lines are consumed to make one. Both travel to where the
//              result will be and fade as it appears, because that is what an
//              addition does: it loses information.
//
// Reduced motion draws the finished move and nothing travels — the arrival is
// the information, the flight is the decoration.
const TRAVEL = 780;

export default function Stage({ step, figures, beat, total, reduced, compact = false }) {
  if (!step) return null;

  const active = beat < total ? beat : null;
  const plan = layout(step, { compact, line: active });
  const result = plan.slots[plan.slots.length - 1];

  // On the cast, each Mother owns four of the sixteen beats.
  const castLanded = (index) => Math.min(Math.max(beat - index * 4, 0), 4);
  const castLanding = (index) => {
    const local = beat - index * 4;
    return local >= 0 && local < 4 ? local : null;
  };

  const flying = reduced ? [] : travellers({ step, layoutResult: plan, figures, line: active });

  return (
    <div className="geo-stage" data-testid="geo-stage" data-kind={step.kind}>
      <svg
        className="geo-stage__canvas"
        viewBox={`-22 0 ${plan.width + 44} ${HEIGHT}`}
        role="img"
        aria-label={`${step.heading}: ${step.summary}`}
        data-testid="geo-stage-canvas"
      >
        {plan.slots.map((slot, index) => {
          const figure = figures[slot.slug];
          const isResult = slot.role === "made" && step.kind !== "cast";
          const landed =
            step.kind === "cast" ? castLanded(index) : isResult ? beat : 4;
          const landing =
            step.kind === "cast" ? castLanding(index) : isResult ? active : null;
          const banded = readingNow(slot, step, active);

          return (
            <g key={slot.key} data-testid={`geo-stage-${slot.key}`}>
              {banded !== null ? (
                <rect
                  className="geo-stage__band"
                  x={slot.x - 6}
                  y={lineY(banded) - 13}
                  width={FIGURE_WIDTH + 12}
                  height={26}
                  rx={4}
                />
              ) : null}

              {figure.lines.map((dots, line) => {
                const here = line < landed;
                const arriving = line === landing;
                // On an addition the result's dots appear as the travellers
                // land, so they wait out the flight.
                const delay = isResult && arriving && !reduced ? TRAVEL : 0;

                return dotXs(dots, slot.x).map((x, dot) => (
                  <circle
                    key={`${line}-${x}`}
                    cx={x}
                    cy={lineY(line)}
                    r={DOT_R}
                    className={[
                      "geo-stage__dot",
                      here ? "geo-stage__dot--here" : "",
                      arriving ? "geo-stage__dot--arriving" : "",
                      !here && !arriving ? "geo-stage__dot--waiting" : ""
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    style={
                      arriving && !reduced
                        ? { animationDelay: `${delay + dot * 170}ms` }
                        : undefined
                    }
                  />
                ));
              })}

              <text className="geo-stage__name" x={slot.x + FIGURE_WIDTH / 2} y={HEIGHT - 32}>
                {figure.printedName}
              </text>
              <text className="geo-stage__label" x={slot.x + FIGURE_WIDTH / 2} y={HEIGHT - 12}>
                {slot.role === "made" && step.kind !== "cast" ? step.heading : slot.short}
              </text>
            </g>
          );
        })}

        {/* The operator between one figure and the next, so the row reads as
            a sentence rather than as figures that happen to be side by side. */}
        {step.kind !== "cast"
          ? plan.slots.slice(0, -1).map((slot, index) => (
              <text
                key={`op-${slot.key}`}
                className="geo-stage__operator"
                x={slot.x + FIGURE_WIDTH + (plan.slots[index + 1].x - slot.x - FIGURE_WIDTH) / 2}
                y={lineY(1.5)}
              >
                {step.kind === "transpose"
                  ? index === plan.slots.length - 2
                    ? "→"
                    : ""
                  : index === plan.slots.length - 2
                    ? "="
                    : "+"}
              </text>
            ))
          : null}

        {/* The dots in flight. Keyed by the beat as well as their place, so a
            line re-entered animates again instead of React reusing an element
            that has already arrived. */}
        {flying.map((one) => (
          <circle
            key={`${one.key}-${beat}`}
            className="geo-stage__travel"
            cx={one.x}
            cy={one.y}
            r={DOT_R}
            style={{
              "--dx": `${one.dx}px`,
              "--dy": `${one.dy}px`,
              animationDuration: `${TRAVEL}ms`,
              animationDelay: `${one.source * 60}ms`
            }}
          />
        ))}
      </svg>
    </div>
  );
}
